<template>
  <div class="preview">
    <img
      v-if="src"
      :src="src"
      :alt="alt"
      :title="alt"
      :class="{ bordered: bordered && !isPlaceholder }"
    />
  </div>
</template>

<script>
import imagePlaceholder from '@/images/image-placeholder.svg'
import { differenceInMinutes } from 'date-fns'
import parseISO from 'date-fns/parseISO'

export default {
  name: 'RetryImage',
  props: {
    url: {
      type: String
    },
    alt: {
      type: String
    },
    timestamp: {
      type: String
    },
    displayPlaceholder: {
      type: Boolean,
      default: true
    },
    // Rendert een <img> in plaats van een background-image, zodat een border
    // strak om de afbeelding valt in plaats van om het (vierkante) vakje.
    bordered: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      src: undefined,
      retryAttemptsLeft: 0,
      retryStarted: false,
      retryWanted: false,
      retryTimeMs: 10000
    }
  },
  computed: {
    isPlaceholder() {
      return this.src === imagePlaceholder
    }
  },
  watch: {
    url: function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadImage(this.url)
      }
    }
  },
  mounted() {
    // Retrying is limited to images not older than 2 minutes

    if (this.timestamp) {
      const difference = differenceInMinutes(
        new Date(),
        parseISO(this.timestamp)
      )
      this.retryWanted = difference < 2
    }

    this.loadImage(this.url)
  },
  methods: {
    loadImage(url) {
      const image = new Image()

      image.onload = () => {
        this.src = url
        this.retryWanted = false
      }

      image.onerror = () => {
        if (this.displayPlaceholder) {
          this.src = imagePlaceholder
        }

        if (!this.retryStarted && this.retryWanted) {
          // Start polling
          this.retryAttemptsLeft = this.retryTimeMs / 1000
          this.retryStarted = true
        }

        if (this.retryAttemptsLeft > 0) {
          this.retry()
        }
      }

      image.src = url
    },
    retry() {
      setTimeout(() => {
        this.loadImage(this.url)
        this.retryAttemptsLeft = this.retryAttemptsLeft - 1
      }, 3000)
    }
  }
}
</script>

<style scoped>
/* Géén object-fit: dat past de afbeelding binnen het element, waardoor het
   element zijn wrapper-vorm houdt en een border om een liggende thumb staand
   wordt. Met auto + max-* krijgt het element de échte vorm van de afbeelding,
   zodat de border strak om de pixels valt. */
.preview {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview img {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
}

/* Alleen wanneer displaySpecification.border true is. Nooit om een
   placeholder-icoon: dat leest als een fout, niet als design. */
.preview img.bordered {
  border: 1px solid var(--image-border-color, #e0e0e0);
  border-radius: 2px;
}
</style>
